import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress } from '@mui/material';
import { func, string, shape, bool } from 'prop-types';
import { compose } from 'recompose';
import { withFormik } from 'formik';

import ErrorPanel from 'components/ErrorPanel';
import { emailValidationSchema } from 'validationSchemas';
import { LOGIN_EMAIL } from 'assets/Icons';
import { passwordResetEmail } from 'views/SignUpForm/scenario-actions';
import { clearError } from 'store/user/actions';
import { errorSelector, isLoadingSelector } from 'store/user/selectors';
import '../login-form.scss';

function PasswordResetEmailSubForm({ values, errors, isValid, setFieldTouched, handleChange }) {
  const dispatch = useDispatch();
  const serverError = useSelector(errorSelector);
  const isLoading = useSelector(isLoadingSelector);
  const [showError, setShowError] = useState(Boolean(serverError));

  useEffect(() => {
    if (serverError) {
      setShowError(true);
    }
  }, [serverError]);

  const resetError = () => {
    if (serverError) {
      dispatch(clearError());
    }
  };

  const disableError = () => {
    setShowError(false);
    dispatch(clearError());
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (!isValid || serverError) {
      setShowError(true);
      return;
    }

    dispatch(
      passwordResetEmail({
        username: values.email,
      }),
    );
  };

  const handleInputChange = (event) => {
    setShowError(false);
    handleChange(event);
  };

  const error = errors.email || serverError;
  const shouldErrorBeDisplayed = showError && !!error;

  return (
    <div className="login-form-page--content">
      <div className="login-form">
        <h2 className="title">Reset password</h2>
        <form onSubmit={onSubmit} className="form-container" autoComplete="off">
          {shouldErrorBeDisplayed && (
            <ErrorPanel message={error} buttonClickHandler={disableError} dataTestId="password-reset-email-error" />
          )}
          <div className="input-container">
            <div className="icon-container">
              <img src={LOGIN_EMAIL} className="input-icon" alt="input-icon" />
            </div>
            <input
              value={values.email}
              name="email"
              placeholder="Email"
              required
              onFocus={resetError}
              onChange={handleInputChange}
              onBlur={(evt) => setFieldTouched(evt.target.name)}
              autoComplete="off"
            />
          </div>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading || !values.email}
              sx={{ fontSize: 18, p: 2 }}
            >
              Reset password
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}

PasswordResetEmailSubForm.propTypes = {
  values: shape({
    email: string,
  }).isRequired,
  errors: shape({
    email: string,
  }),
  isValid: bool.isRequired,
  setFieldTouched: func.isRequired,
  handleChange: func.isRequired,
};

export default compose(
  withFormik({
    mapPropsToValues: () => ({
      email: '',
    }),
    validationSchema: emailValidationSchema,
  }),
)(PasswordResetEmailSubForm);
